import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronsUpDown,
  LogOut,
  Printer,
  Settings,
  User as UserIcon
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar'
import { useAuthStore } from '@/app/store/authStore'
import { ProfilePopup } from '@/app/pages/profile/components/ProfilePopup'
import { BasicPrinterSettings } from '@/app/components/BasicPrinterSettings'

// Initials for the avatar fallback: first + last word of the display name.
const getInitials = (name?: string | null): string => {
  if (!name) return 'U'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Account entry for the sidebar footer. This is the single home for
 * profile / settings / printer / logout — the header no longer carries them.
 */
export const NavUser: React.FC = () => {
  const { user, logoutAction } = useAuthStore()
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false)
  const [isPrinterSettingsOpen, setIsPrinterSettingsOpen] = useState(false)

  const displayName = user?.full_name || user?.name || user?.username || 'User'
  const secondaryLine = user?.email || user?.username || ''
  const initials = getInitials(displayName)

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 shrink-0 rounded-lg">
                  <AvatarImage src="/placeholder-user.jpg" alt={displayName} />
                  <AvatarFallback className="rounded-lg bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{displayName}</span>
                  {secondaryLine && (
                    <span className="truncate text-xs text-sidebar-muted">{secondaryLine}</span>
                  )}
                </div>
                <ChevronsUpDown className="ml-auto size-4 text-sidebar-muted" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src="/placeholder-user.jpg" alt={displayName} />
                    <AvatarFallback className="rounded-lg text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{displayName}</span>
                    {secondaryLine && (
                      <span className="truncate text-xs text-muted-foreground">{secondaryLine}</span>
                    )}
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onSelect={() => setIsProfilePopupOpen(true)}
                  className="cursor-pointer"
                >
                  <UserIcon className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate('/settings/organization-settings')}
                  className="cursor-pointer"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    setIsPrinterSettingsOpen(true)
                  }}
                  className="cursor-pointer"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  <span>Printer Settings</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => logoutAction()} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <ProfilePopup isOpen={isProfilePopupOpen} onOpenChange={setIsProfilePopupOpen} />
      <BasicPrinterSettings
        isOpen={isPrinterSettingsOpen}
        onClose={() => setIsPrinterSettingsOpen(false)}
      />
    </>
  )
}
